import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { axiosInstance } from "../../../../Utils/AxiosInstance";
import { revertAll } from "../../ResetStateAction";

const SELECTED_PIPELINE_KEY = "selectedPipeline";

// sessionStorage rather than localStorage: a pipeline Id belongs to one business,
// so the selection must not outlive the session the way access_token does not.
// Every access is wrapped -- private mode and storage-disabled browsers throw on
// read as well as on write.
const readSelectedPipeline = () => {
  try {
    const raw = sessionStorage.getItem(SELECTED_PIPELINE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const writeSelectedPipeline = pipeline => {
  try {
    if (pipeline?.Id) sessionStorage.setItem(SELECTED_PIPELINE_KEY, JSON.stringify(pipeline));
    else sessionStorage.removeItem(SELECTED_PIPELINE_KEY);
  } catch {
    // Storage unavailable -- Redux still holds the selection for this page load.
  }
};

const initialState = {
  Piplines: [],
  // Pipeline currently selected on the Leads screen. Read by the Create Lead
  // screen so a new lead lands in that pipeline instead of the business default.
  // Hydrated from sessionStorage so it survives a hard refresh.
  SelectedPipeline: readSelectedPipeline(),
  isSuccess: false,
  message: null,
  statusPipeline: "idle", //'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  IsLogin: true,
};

export const fetchPiplines = createAsyncThunk("/LeadPipeline", async () => {
  const response = await axiosInstance.get(`/LeadPipeline`);
  return response.data;
});

const PiplinesSlice = createSlice({
  name: "Piplines",
  initialState,
  reducers: {
    setSelectedPipeline: (state, action) => {
      const pipeline = action.payload || null;
      state.SelectedPipeline = pipeline;
      writeSelectedPipeline(pipeline);
    },
    PiplinesAdded: {
      reducer(state, action) {
        state.Piplines?.push(action.payload);
      },
    },
  },
  extraReducers(builder) {
    builder
      .addCase(revertAll, () => {
        // initialState captured the hydrated value at import time, so the stored
        // selection has to be dropped explicitly on logout.
        writeSelectedPipeline(null);
        return { ...initialState, SelectedPipeline: null };
      })
      .addCase(fetchPiplines.pending, state => {
        state.statusPipeline = "loading";
      })
      .addCase(fetchPiplines.fulfilled, (state, action) => {
        state.statusPipeline = "succeeded";
        state.message = action.payload.Message;
        state.isSuccess = action.payload.IsSuccess;
        state.Piplines = action.payload.Data;

        // Reconcile a persisted selection against what this user can actually see.
        // /LeadPipeline is scoped to the caller's business server-side, so a
        // pipeline belonging to another business simply is not in this list and
        // gets dropped. A match is replaced with the fresh copy so renamed or
        // reordered stages are picked up -- the entry stage is derived from
        // SortOrder, so a stale copy would target the wrong stage.
        const stored = state.SelectedPipeline;
        if (stored?.Id) {
          const fresh = action.payload.Data?.find(p => p?.Id === stored.Id) || null;
          state.SelectedPipeline = fresh;
          writeSelectedPipeline(fresh);
        }
      })
      .addCase(fetchPiplines.rejected, (state, action) => {
        state.error = action.error.message;
        state.statusPipeline = "failed";
      });
  },
});

export const getPiplinesDetails = state => state.Piplines;

export const getSelectedPipeline = state => state.Piplines.SelectedPipeline;

export const { PiplinesAdded, setSelectedPipeline } = PiplinesSlice.actions;

export default PiplinesSlice.reducer;
